import { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, Container, Typography, ButtonGroup } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Formik, Form, FormikValues, FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import {
    useNavigate,
    useBeforeUnload,
    useSearchParams,
} from "react-router-dom";
import { FormInput, Logo, Footer, Splashscreen } from "src/components";
import { getService } from "src/utils/helpers";
import { recoverySchema, codeSchema, passwordResetSchema } from "../schema";
import { useTitle } from "react-use";
import { PassioService } from "src/types";
import { authAPI, authSelector, logout } from "../redux/authSlice";
import { useAppSelector, useAppDispatch } from "src/hooks";

/**
 * @param agency: agency name
 * @param service paraplan | navigator
 * @example /login?agency=ENGRAPHTRANSIT&service=paraplan
 */

type Step = "email" | "verify" | "reset";
interface Cache {
    id: string;
    step: Step;
    email: string;
    resend: boolean;
}

const initialCache: Cache = {
    id: "",
    email: "",
    step: "email",
    resend: false,
};

const PasswordReset = () => {
    useTitle("Password Recovery");
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { config } = useAppSelector(authSelector);
    const [searchParams] = useSearchParams();
    const checked = useRef(false);
    const code = searchParams.get("code");
    const id = searchParams.get("id");

    const [cache, setCache] = useState<Cache>(initialCache);

    const [sendReset, { isLoading: isSendingEmail }] =
        authAPI.useSendResetPasswordMutation();

    const [verifyResetCode, { isLoading: verifying }] =
        authAPI.useVerifyResetCodeMutation();

    const [resetPassword, { isLoading: changingPassword }] =
        authAPI.useResetPasswordMutation();

    const loading = isSendingEmail || verifying || changingPassword;

    // always clear cache when the user leaves this page
    useBeforeUnload(
        useCallback(() => {
            dispatch(logout());
        }, [dispatch])
    );

    async function sendResetCode(
        { email }: FormikValues,
        helpers: FormikHelpers<any>
    ) {
        try {
            const { agencyId, id } = await sendReset(email).unwrap();
            enqueueSnackbar(
                `A verification email was sent to ${email}. Please check it to verify your account and reset your password`,
                { variant: "success" }
            );

            if (agencyId && !config?.AgencyName) {
                await dispatch(authAPI.endpoints.getConfig.initiate(agencyId));
            }

            setCache((cache) => ({
                ...cache,
                email,
                id,
                step: "verify",
            }));
        } catch (error) {
            enqueueSnackbar(
                "Sorry we couldn't verify your account. Please try again",
                { variant: "error" }
            );
        }
    }

    async function verifyCode(
        { code }: FormikValues,
        helpers: FormikHelpers<any>
    ) {
        try {
            await verifyResetCode({
                code,
                id: cache.id,
            }).unwrap();
            setCache((cache) => ({ ...cache, resend: false, step: "reset" }));
            enqueueSnackbar("Account verified successfully", {
                variant: "success",
            });
        } catch (error) {
            const message =
                "Sorry the code entered is invalid. Please try again";
            enqueueSnackbar(message, { variant: "error" });
            helpers.setFieldError("code", message);
            setCache((cache) => ({ ...cache, resend: true }));
        }
    }

    async function resendCode() {
        try {
            if (!cache.email) {
                enqueueSnackbar("Sorry your session expired please try again", {
                    variant: "info",
                });
                setCache((cache) => ({
                    ...cache,
                    resend: false,
                    step: "email",
                }));
            }
            await sendReset(cache.email).unwrap();
            enqueueSnackbar(
                `A verification email was sent to ${cache.email}. Please check it to verify your account and reset your password`,
                { variant: "success" }
            );
        } catch (error) {
            enqueueSnackbar(
                "Sorry an error occured. Please refresh the page and try again",
                { variant: "error" }
            );
        }
    }

    async function changePassword({ password }: FormikValues) {
        try {
            await resetPassword({ id: cache.id, password }).unwrap();
            enqueueSnackbar("Password updated successfully. Please login", {
                variant: "success",
            });
            navigate("/login");
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Sorry an error occured please try again", {
                variant: "error",
            });
        }
    }

    const handleAutoVerify = useCallback(async () => {
        if (code && id) {
            try {
                const { agencyId } = await verifyResetCode({
                    id,
                    code,
                }).unwrap();

                if (agencyId && !config?.AgencyName) {
                    await dispatch(
                        authAPI.endpoints.getConfig.initiate(agencyId)
                    );
                }
                setCache((cache) => ({
                    ...cache,
                    id,
                    resend: false,
                    step: "reset",
                }));
                enqueueSnackbar("Account verified successfully", {
                    variant: "success",
                });
            } catch (error) {
                enqueueSnackbar(
                    "Sorry you code may have expired, please try again",
                    { variant: "error" }
                );
                setCache((cache) => ({ ...cache, id, resend: true }));
            }
        }
    }, [code, id, config, dispatch, verifyResetCode]);

    useEffect(() => {
        if (getService() !== PassioService.navigator) navigate("/login");
    }, [navigate]);

    // check queryParams
    useEffect(() => {
        if (!checked.current) handleAutoVerify();
        checked.current = true;
    }, [handleAutoVerify]);

    if (verifying && !checked.current) {
        return <Splashscreen description="Processing ..." />;
    }

    if (cache.step === "verify") {
        return (
            <Container
                component="main"
                maxWidth="xs"
                sx={{ display: "grid", placeItems: "center", height: "100vh" }}
            >
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Logo />

                    <Typography component="h2" variant="h5" my={1}>
                        Account Verification
                    </Typography>

                    <Typography my={3} textAlign="center">
                        Please check your email for a verification code and
                        enter it below
                    </Typography>

                    <Formik
                        initialValues={{ code: searchParams.get("code") || "" }}
                        validationSchema={codeSchema}
                        onSubmit={verifyCode}
                    >
                        <Box component={Form} sx={{ mt: 4 }}>
                            <FormInput
                                fullWidth
                                name="code"
                                label="Verification code"
                                margin="normal"
                                sx={{ mb: 2 }}
                            />

                            <Button
                                size="large"
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    my: 2,
                                    py: 1,
                                    color: "primary.contrastText",
                                }}
                            >
                                verify
                            </Button>

                            <ButtonGroup variant="text" fullWidth>
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setCache((cache) => ({
                                            ...cache,
                                            step: "email",
                                        }));
                                    }}
                                >
                                    Cancel
                                </Button>
                                {cache.resend && (
                                    <LoadingButton
                                        type="button"
                                        disabled={loading}
                                        loading={loading}
                                        onClick={resendCode}
                                    >
                                        Resend Code
                                    </LoadingButton>
                                )}
                            </ButtonGroup>
                        </Box>
                    </Formik>
                </Box>
                <Footer />
            </Container>
        );
    }

    if (cache.step === "reset") {
        return (
            <Container
                component="main"
                maxWidth="xs"
                sx={{ display: "grid", placeItems: "center", height: "100vh" }}
            >
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Logo />

                    <Typography component="h2" variant="h5" my={1}>
                        Password Reset
                    </Typography>

                    <Typography my={3} textAlign="center">
                        Please enter your new account password
                    </Typography>

                    <Formik
                        initialValues={{ password: "", confirmPassword: "" }}
                        validationSchema={passwordResetSchema}
                        onSubmit={changePassword}
                    >
                        <Box component={Form} sx={{ mt: 4 }}>
                            <FormInput
                                fullWidth
                                name="password"
                                label="New Password"
                                type="password"
                                margin="normal"
                            />
                            <FormInput
                                fullWidth
                                name="confirmPassword"
                                label="Confirm Password"
                                type="password"
                                margin="normal"
                                sx={{ mb: 2 }}
                            />

                            <LoadingButton
                                size="large"
                                type="submit"
                                fullWidth
                                variant="contained"
                                loading={loading}
                                disabled={loading}
                                sx={{
                                    my: 2,
                                    py: 1,
                                    color: "primary.contrastText",
                                }}
                            >
                                Continue
                            </LoadingButton>

                            <Button
                                type="button"
                                fullWidth
                                onClick={() => {
                                    setCache(initialCache);
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Formik>
                </Box>
                <Footer />
            </Container>
        );
    }

    return (
        <Container
            component="main"
            maxWidth="xs"
            sx={{ display: "grid", placeItems: "center", height: "100vh" }}
        >
            <Box
                sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Logo />
                <Typography component="h2" variant="h5">
                    Password Recovery
                </Typography>

                <Formik
                    initialValues={{ email: "" }}
                    validationSchema={recoverySchema}
                    onSubmit={sendResetCode}
                >
                    <Box component={Form} sx={{ mt: 4 }}>
                        <FormInput
                            fullWidth
                            name="email"
                            label="Email address"
                            margin="normal"
                            inputProps={{ autoComplete: "email" }}
                            sx={{ mb: 2 }}
                        />

                        <LoadingButton
                            size="large"
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            loading={loading}
                            sx={{ my: 2, py: 1, color: "primary.contrastText" }}
                        >
                            {loading ? "Verifying" : "Continue"}
                        </LoadingButton>

                        <Button
                            variant="text"
                            type="button"
                            fullWidth
                            href="/login"
                            sx={{ mb: 2 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Formik>
            </Box>
            <Footer />
        </Container>
    );
};

export default PasswordReset;
